import { BatteryFull, Signal, Wifi } from "lucide-react";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   PhoneFrame – a realistic handset around real, live UI. The screen is
   laid out at 320×660 CSS px and scaled with `scale`, so everything
   inside renders exactly as on a phone. `dark` re-scopes the Drift
   tokens for the screen only.
   ------------------------------------------------------------------ */

export const PHONE_W = 338;
export const PHONE_H = 692;

export function PhoneFrame({ children, scale = 1, dark = false, time = "09:41", className, testid = "phone-frame", interactive = true }) {
  return (
    <div className={cn("relative shrink-0", className)} style={{ width: PHONE_W * scale, height: PHONE_H * scale }} data-testid={testid} data-scale={scale}>
      <div className="phone absolute left-0 top-0 origin-top-left" style={{ width: PHONE_W, height: PHONE_H, transform: `scale(${scale})` }}>
        {/* side buttons */}
        <span className="phone-btn absolute -left-[3px] top-[118px] h-[26px] w-[3px] rounded-l-[2px]" aria-hidden />
        <span className="phone-btn absolute -left-[3px] top-[170px] h-[52px] w-[3px] rounded-l-[2px]" aria-hidden />
        <span className="phone-btn absolute -left-[3px] top-[236px] h-[52px] w-[3px] rounded-l-[2px]" aria-hidden />
        <span className="phone-btn absolute -right-[3px] top-[192px] h-[84px] w-[3px] rounded-r-[2px]" aria-hidden />
        {/* body */}
        <div className="phone-body absolute inset-0 rounded-[54px]">
          <div className={cn("phone-screen absolute inset-[9px] flex flex-col overflow-hidden rounded-[46px] bg-background text-foreground", dark && "dark")} data-testid="phone-screen">
            {/* dynamic island */}
            <span className="phone-island absolute left-1/2 top-[11px] z-20 h-[30px] w-[100px] -translate-x-1/2 rounded-full" aria-hidden />
            {/* status bar */}
            <div className="relative z-10 flex h-[52px] shrink-0 items-end justify-between px-8 pb-1 text-[14px] font-semibold tabular" aria-hidden>
              <span className="pl-1">{time}</span>
              <span className="flex items-center gap-1.5 pr-0.5"><Signal className="h-3.5 w-3.5" strokeWidth={2.25} /><Wifi className="h-3.5 w-3.5" strokeWidth={2.25} /><BatteryFull className="h-4 w-4" strokeWidth={2} /></span>
            </div>
            {/* app */}
            <div className={cn("min-h-0 flex-1 overflow-y-auto scrollbar-none", !interactive && "pointer-events-none")}>{children}</div>
            {/* home indicator */}
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 flex h-6 items-end justify-center pb-2" aria-hidden><span className="h-[5px] w-[124px] rounded-full bg-foreground/85" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Small app header shown inside the phone (mirrors MontorLayout's top bar). */
export function PhoneAppBar({ user }) {
  const ini = (user?.initials || (user?.name || "").split(" ").map((s) => s[0]).join("")).slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center justify-between px-4 pb-1 pt-1">
      <span className="inline-flex items-center gap-2 font-display text-[14.5px] tracking-[-0.02em]">
        <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden><rect width="32" height="32" rx="8" fill="hsl(var(--brand))" /><path d="M10.5 8v16" stroke="hsl(var(--ink))" strokeWidth="4" strokeLinecap="round" /><path d="M22.5 8.5 15.5 16l7 7.5" stroke="hsl(var(--ink))" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
        <span className="font-semibold">Drift</span>
      </span>
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-foreground font-display text-[10.5px] font-semibold text-background">{ini}</span>
    </div>
  );
}
